import type {
  ClientBuilderPlanGltfTiming,
  ClientBuilderPlanGltfTimingSpan,
} from "api-types/builders/buildersResponses";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { GenerateBuildingFeatureSet } from "building-types/building/buildingTypes";
import type { ShowroomFloorPlanPayload } from "building-types/building/showroomFloorPlanPayloadTypes";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { MaterialSrc } from "common/materials/materialSrc";
import type { BuildingRenderPayload } from "gfx/buildingRenderPayload";
import type { GLTFDesignMaterialParamsByKey } from "gfx/gltf/gltfBuildingExtras";
import type {
  ShowroomOptionCatalogConfig,
  ShowroomOptionCatalogFingerprints,
  ShowroomOptionCatalogSnapshot,
} from "lib/components/showroom2/state/showroomTypes";

export const SHOWROOM_WORKER_HEARTBEAT_INTERVAL_MS = 5_000;
export const SHOWROOM_WORKER_HEARTBEAT_TIMEOUT_MS = 45_000;

export type ShowroomOptionStateProtocol = "snapshot" | "optionStateV2";

/**
 * Identifies a plan for fetching `/public-plans` and generating its render payload.
 */
export interface PlanIdentifiers {
  builderId: string;
  planType: "community_plans" | "master_plans";
  planId: string;
  lotId?: string;
}

/**
 * Messages sent from main thread to showroom building worker.
 *
 * The worker holds session state in a module-level closure. Production initializes from
 * `/public-plans` identifiers; debug tooling may initialize from injected plan data.
 * Every message carries a `sessionId` so the worker (and the hook on the way back) can
 * discard stale work triggered by rapid plan navigation or concurrent mounts. Active
 * requests also carry a monotonically increasing `requestId` so replies, including
 * heartbeat and idle messages, are matched to the current request and cannot affect
 * newer work in the same session.
 */
export type MainToShowroomBuildingWorkerMessage =
  | InitializeShowroomWorkerMessage
  | UpdateOptionsShowroomWorkerMessage
  | ApplyOptionChoiceShowroomWorkerMessage
  | ResetShowroomWorkerMessage;

interface InitializeShowroomWorkerMessageBase {
  type: "initialize";
  sessionId: string;
  requestId: number;
  enabledFeatures: GenerateBuildingFeatureSet;
  /** Direct option intents are accepted only when this is explicitly `optionStateV2`. */
  optionStateProtocol?: ShowroomOptionStateProtocol;
  /** Inputs that affect exact Showroom catalog derivation in worker-owned mode. */
  catalogConfig?: ShowroomOptionCatalogConfig;
}

export interface InitializeShowroomWorkerByIdentifiersMessage extends InitializeShowroomWorkerMessageBase {
  identifiers: PlanIdentifiers;
}

export interface InitializeShowroomWorkerByPlanDataMessage extends InitializeShowroomWorkerMessageBase {
  planData: ClientHomePlanBaseData;
}

export type InitializeShowroomWorkerMessage =
  | InitializeShowroomWorkerByIdentifiersMessage
  | InitializeShowroomWorkerByPlanDataMessage;

export interface UpdateOptionsShowroomWorkerMessage {
  type: "updateOptions";
  sessionId: string;
  requestId: number;
  /** Full option-choice set. No patch protocol. */
  optionChoices: OptionChoices;
  /** Choices from the current user action, used to scope overridable option rules. */
  activeChoices?: OptionChoices;
  /** Selected reporting unit for unit-scoped showroom floor-plan sheet geometry. */
  selectedReportingUnitLabelId?: string;
}

export interface ApplyOptionChoiceShowroomWorkerMessage {
  type: "applyOptionChoice";
  sessionId: string;
  requestId: number;
  optionSetId: string;
  /** The explicit option value, including the string value `"false"`. */
  optionId: string;
  /** Selected reporting unit for unit-scoped showroom floor-plan sheet geometry. */
  selectedReportingUnitLabelId?: string;
}

/** Applies one worker-owned intent to the last resolved option-choice snapshot. */
export function optionChoicesWithShowroomIntent(
  resolvedOptionChoices: OptionChoices,
  intent: Pick<ApplyOptionChoiceShowroomWorkerMessage, "optionSetId" | "optionId">,
): OptionChoices {
  return {
    ...resolvedOptionChoices,
    [intent.optionSetId]: intent.optionId,
  };
}

export interface ResetShowroomWorkerMessage {
  type: "reset";
  /** Drops the session if it matches; otherwise no-op. */
  sessionId: string;
}

/**
 * Messages sent from showroom building worker to main thread.
 */
export type ShowroomBuildingWorkerToMainMessage =
  | ShowroomBuildingWorkerInitializedMessage
  | ShowroomBuildingWorkerOptionStateResolvedMessage
  | ShowroomBuildingWorkerCatalogResolvedMessage
  | ShowroomBuildingWorkerMaterialWarmupMessage
  | ShowroomBuildingWorkerUpdatedMessage
  | ShowroomBuildingWorkerFloorPlanMessage
  | ShowroomBuildingWorkerErrorMessage
  | ShowroomBuildingWorkerHeartbeatMessage
  | ShowroomBuildingWorkerIdleMessage;

export type ShowroomBuildingWorkerTiming = ClientBuilderPlanGltfTimingSpan;

export interface ShowroomWorkerBuildingGenerationStageTiming extends ClientBuilderPlanGltfTimingSpan {
  stageId: string;
  startTimeMs: number;
}

export type ShowroomWorkerBuildingTiming = Omit<ClientBuilderPlanGltfTiming, "gltfSerialization"> & {
  buildingGenerationStages: ShowroomWorkerBuildingGenerationStageTiming[];
  renderPayloadAssembly: ClientBuilderPlanGltfTimingSpan;
  geometryHashing?: ClientBuilderPlanGltfTimingSpan;
};

export type ShowroomBuildingWorkerErrorPhase = "initialize" | "updateOptions" | "applyOptionChoice" | "floorPlan";

export type ShowroomWorkerFailurePhase =
  | ShowroomBuildingWorkerErrorPhase
  | "startup"
  | "runtime"
  | "messageerror"
  | "renderPayloadHydration"
  | "heartbeatTimeout"
  | "initializePostMessage"
  | "updateOptionsPostMessage"
  | "applyOptionChoicePostMessage"
  | "resetPostMessage";

export interface ShowroomBuildingWorkerErrorDetails {
  name: string;
  message: string;
  stack?: string;
}

/** Normalizes thrown values for worker transport and main-thread error reporting. */
export function showroomBuildingWorkerErrorDetailsFromUnknown(
  error: unknown,
  fallbackMessage: string = "Worker error",
): ShowroomBuildingWorkerErrorDetails {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  if (typeof error === "object" && error !== null) {
    const errorLike = error as { name?: unknown; message?: unknown; stack?: unknown };
    if (typeof errorLike.message === "string") {
      return {
        name: typeof errorLike.name === "string" ? errorLike.name : "Error",
        message: errorLike.message,
        stack: typeof errorLike.stack === "string" ? errorLike.stack : undefined,
      };
    }
  }
  return {
    name: "Error",
    message: error === null || error === undefined ? fallbackMessage : String(error),
  };
}

export interface ShowroomBuildingWorkerError extends ShowroomBuildingWorkerErrorDetails {
  phase: ShowroomBuildingWorkerErrorPhase;
}

interface BaseWorkerReply {
  /** Echoes the session that produced this reply. */
  sessionId: string;
  /** Echoes the request that produced this reply. */
  requestId: number;
}

export interface ShowroomBuildingWorkerInitializedMessage extends BaseWorkerReply {
  type: "initialized";
  /** Plan data fetched by the worker from `/public-plans`. Includes `homePlan.doc`. */
  planData: ClientHomePlanBaseData;
}

export interface ShowroomBuildingWorkerOptionStateResolvedMessage extends BaseWorkerReply {
  type: "optionStateResolved";
  /** Choice snapshot presented to option-state preparation. */
  requestedOptionChoices: OptionChoices;
  /** Choices after option rules, locks, and sanitization are applied. */
  resolvedOptionChoices: OptionChoices;
}

export interface ShowroomBuildingWorkerCatalogResolvedMessage extends BaseWorkerReply {
  type: "catalogResolved";
  /** Final choices from the generated Building represented by this catalog. */
  resolvedOptionChoices: OptionChoices;
  snapshot: ShowroomOptionCatalogSnapshot;
  optionSetFingerprints: ShowroomOptionCatalogFingerprints;
}

export interface ShowroomBuildingWorkerMaterialWarmupMessage extends BaseWorkerReply {
  type: "materialWarmup";
  /** Unique material sources used by the geometry generated for this option set. */
  materialSources: MaterialSrc[];
  /** Source-resolved params so the main thread can avoid cold template/layer config fetches. */
  designMaterialParamsByKey: GLTFDesignMaterialParamsByKey;
  /**
   * False for the early manifest posted right after building generation (palette-derived
   * sources, no params yet); true for the post-collection manifest with exact emitter
   * sources and resolved params. The final manifest signals the render payload is imminent.
   */
  final: boolean;
  /** Prop asset ids used by the building (builtin ids are numeric), for main-thread prop GLB prefetching. */
  propAssetIds?: (number | string)[];
}

export interface ShowroomBuildingWorkerUpdatedMessage extends BaseWorkerReply {
  type: "updated";
  /** Choice snapshot that requested this generated building. */
  requestedOptionChoices: OptionChoices;
  renderPayload: BuildingRenderPayload;
  timing: ShowroomWorkerBuildingTiming;
}

/**
 * Sheet-geometry floor plan, refreshed for the new option choices. Posted after `updated`
 * so floor-plan generation does not delay the 3D handoff.
 */
export interface ShowroomBuildingWorkerFloorPlanMessage extends BaseWorkerReply {
  type: "floorPlan";
  floorPlan: ShowroomFloorPlanPayload;
  timing: ShowroomBuildingWorkerTiming;
}

export interface ShowroomBuildingWorkerErrorMessage extends BaseWorkerReply {
  type: "error";
  error: ShowroomBuildingWorkerError;
}

export interface ShowroomBuildingWorkerHeartbeatMessage extends BaseWorkerReply {
  type: "heartbeat";
}

export interface ShowroomBuildingWorkerIdleMessage extends BaseWorkerReply {
  type: "idle";
}
